import {
    ICalloutContentStyleProps,
    ICalloutContentStyles,
    ISpinnerStyleProps,
    ISpinnerStyles,
} from 'office-ui-fabric-react/lib';
import { generateColorScheme, generateDefaultColorSchemes } from 'owa-color-utils/lib/colorUtils';
import { getPalette, getPaletteAsRawColors } from 'owa-theme';

import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { getOptimalTextColorString } from 'owa-color-utils/lib/colorUtils';
import { mergeStyleSets } from '@uifabric/styling';

let classNames = require('classnames');

const colorIndexOrder: number[] = [0, 5, 1, 3, 8, 6, 7, 2, 4];
let allColors: ColorScheme[];
let availableColors: ColorScheme[];

/**
 * Generates color scheme array in color order
 * @returns all color schemes in color order
 */
export function generateAvailableColorsInOrder(): ColorScheme[] {
    return colorIndexOrder.map<ColorScheme>(colorIndex => {
        return getAllColors()[colorIndex];
    });
}

export default function colorWrapper(
    colorScheme: ColorScheme,
    component:
        | 'DefaultButton'
        | 'IconButton'
        | 'Spinner'
        | 'PeoplePickerSuggestionProps'
        | 'Dropdown'
        | 'NormalPeoplePickerBase',
    classname?: string
): any {
    var colorStyles = {};
    if (component == 'DefaultButton') {
        colorStyles = {
            root: {
                backgroundColor: colorScheme.calPrimary,
            },
            rootHovered: {
                backgroundColor: colorScheme.calDarkAlt,
            },
            rootPressed: {
                backgroundColor: colorScheme.calDark,
            },
        };
    } else if (component == 'IconButton') {
        colorStyles = {
            iconHovered: {
                color: getOptimalTextColorStringWrapper(colorScheme.calDarker, colorScheme),
            },
            iconPressed: {
                color: getOptimalTextColorStringWrapper(colorScheme.calPrimary, colorScheme),
            },
        };
    } else if (component == 'Spinner') {
        colorStyles = (props: ISpinnerStyleProps): ISpinnerStyles => {
            return {
                circle: {
                    borderTopColor: colorScheme.calDark,
                    borderBottomColor: colorScheme.calLighter,
                    borderLeftColor: colorScheme.calLighter,
                    borderRightColor: colorScheme.calLighter,
                },
            };
        };
    } else if (component == 'PeoplePickerSuggestionProps') {
        let pickerSuggestionStyle = mergeStyleSets({
            root: {
                selectors: {
                    //is-suggested class is added on keypress
                    '.ms-Suggestions-container .is-suggested': {
                        background: colorScheme.calLighter,
                    },
                    '.ms-Suggestions-container .ms-Suggestions-item.is-suggested:hover': {
                        background: getPaletteAsRawColors().neutralLighter,
                    },
                    '.ms-SearchMore-button .ms-Button-icon, .ms-SearchMore-button:hover .ms-Button-icon, .ms-SearchMore-button:active .ms-Button-icon': {
                        color: getOptimalTextColorStringWrapper(
                            colorScheme.calPrimary,
                            colorScheme
                        ),
                    },
                    '.ms-Spinner-circle': {
                        borderTopColor: colorScheme.calDark,
                        borderBottomColor: colorScheme.calLighter,
                        borderLeftColor: colorScheme.calLighter,
                        borderRightColor: colorScheme.calLighter,
                    },
                    '.ms-SearchMore-button:hover': {
                        color: getPaletteAsRawColors().neutralPrimary,
                    },
                    '.ms-SearchMore-button.is-selected': {
                        background: colorScheme.calLighter,
                    },
                },
            },
        });
        colorStyles = classNames(classname, pickerSuggestionStyle.root);
    } else if (component == 'Dropdown') {
        let dropdownContainerStyle = mergeStyleSets({
            root: {
                selectors: {
                    '.ms-Dropdown&:focus .ms-Dropdown-title': {
                        borderColor: getOptimalTextColorStringWrapper(
                            colorScheme.calPrimary,
                            colorScheme
                        ),
                    },
                },
            },
        });
        colorStyles = {
            calloutProps: {
                getStyles: (props: ICalloutContentStyleProps): ICalloutContentStyles => {
                    return {
                        //ICalloutContentStyles fields are not optional in fabric.
                        container: {},
                        root: {},
                        beak: {},
                        beakCurtain: {},
                        calloutMain: {
                            selectors: {
                                '.ms-Dropdown-items .is-selected': {
                                    backgroundColor: colorScheme.calLighter,
                                    color: '#000000',
                                },
                            },
                        },
                    };
                },
            },
            className: classNames(classname, dropdownContainerStyle.root),
        };
    } else if (component == 'NormalPeoplePickerBase') {
        let pickerSuggestionStyle = mergeStyleSets({
            root: {
                selectors: {
                    input: {
                        backgroundColor: getPalette().white,
                    },
                },
            },
        });
        colorStyles = classNames(classname, pickerSuggestionStyle.root);
    }
    return colorStyles;
}

export function getColorSchemeForHexColor(hexColor: string): ColorScheme {
    let assignedCalendarColors = null;
    //If calendar color is automatic
    if (!hexColor || hexColor == '#000000') {
        //Remove a color from the set of available colors
        assignedCalendarColors = getAvailableColors().pop();
    } else {
        assignedCalendarColors = getAllColors().filter(colorItem => {
            return colorItem.calPrimary.toUpperCase() == hexColor.toUpperCase();
        })[0];
        if (assignedCalendarColors) {
            removeHexColorFromAvailableColors(hexColor);
        } else {
            assignedCalendarColors = generateColorScheme(hexColor);
        }
    }
    //If we have more calendars than the set of available colors, we refresh the list
    if (!getAvailableColors().length) {
        availableColors = generateAvailableColorsInOrder().reverse();
    }
    return assignedCalendarColors;
}

export function getHexcolorForAutomaticColorScheme(hexColor: string): string {
    let resultHexColor = '';
    if (!hexColor || hexColor == '#000000') {
        resultHexColor = getAvailableColors().pop().calPrimary;
        //If we have more calendars than the set of available colors, we refresh the list
        if (!getAvailableColors().length) {
            availableColors = generateAvailableColorsInOrder().reverse();
        }
    } else {
        removeHexColorFromAvailableColors(hexColor);
        resultHexColor = hexColor;
    }
    return resultHexColor;
}

function getAllColors(): ColorScheme[] {
    if (!allColors) {
        allColors = generateDefaultColorSchemes();
    }

    return allColors;
}

function getAvailableColors(): ColorScheme[] {
    if (!availableColors) {
        availableColors = generateAvailableColorsInOrder().reverse();
    }

    return availableColors;
}

function getOptimalTextColorStringWrapper(color: string, colorScheme: ColorScheme) {
    return getOptimalTextColorString(getPaletteAsRawColors().neutralLighterAlt, colorScheme, color);
}

function removeHexColorFromAvailableColors(hexColor: string) {
    let availableColorsEntry;
    for (let i = 0; i < getAvailableColors().length; i++) {
        if (getAvailableColors()[i].calPrimary == hexColor) {
            availableColorsEntry = getAvailableColors()[i];
            break;
        }
    }
    if (availableColorsEntry) {
        //If we have a hexcolor passed we have to remove it from the set of available colors; since we dont want to assign
        //same colors to calendars.
        getAvailableColors().splice(availableColorsEntry.index, 1);
    }
    //If we have more calendars than the set of available colors, we refresh the list
    if (!getAvailableColors().length) {
        availableColors = generateAvailableColorsInOrder().reverse();
    }
}
